import { ErrorPayload } from "../../../common/payloads/ErrorActionPayload";
import { Action, handleActions } from "redux-actions";
import fetchTaxYears from "../../../api/sustainability/taxYears/get";
interface TaxYear {
  id: number;
  name: string;
  year: number;
}
interface TaxYearsState {
  isFetching: boolean;
  data: any[];
  currentYear?: number;
}

// State Interface
export interface MainSustainabilityState {
  isSimulation: boolean;
  isJoint: boolean;
  isFirstSplit: boolean;
  taxYears: TaxYearsState;
  splitYear?: [TaxYear, TaxYear];
  activePeriod?: TaxYear;
  error?: any;
}

// Initial state
const initialState = {
  isSimulation: false,
  isJoint: false,
  isFirstSplit: true,
  taxYears: {
    isFetching: false,
    data: [],
    error: undefined,
  } as TaxYearsState,
  error: undefined,
} as MainSustainabilityState;

// Actions
const SUSTAINABILITY_TOGGLE_SIMULATION = "sustainability/TOGGLE_SIMULATION";
const SUSTAINABILITY_TOGGLE_JOINT = "sustainability/TOGGLE_JOINT";
const SUSTAINABILITY_TOGGLE_SPLIT_YEAR = "sustainability/TOGGLE_SPLIT_YEAR";
const TAX_YEARS_REQUEST = "sustainability/taxYears/REQUEST";
const TAX_YEARS_ERROR = "sustainability/taxYears/ERROR";
const TAX_YEARS_SUCCESS = "sustainability/taxYears/SUCCESS";
// Action creators
export function toggleSimulation() {
  return {
    type: SUSTAINABILITY_TOGGLE_SIMULATION,
  };
}

export function toggleJoint() {
  return {
    type: SUSTAINABILITY_TOGGLE_JOINT,
  };
}

export function toggleSplitYear() {
  return {
    type: SUSTAINABILITY_TOGGLE_SPLIT_YEAR,
  };
}

function requestTaxYears() {
  return {
    type: TAX_YEARS_REQUEST,
  };
}

function errorTaxYears(error: any) {
  return {
    type: TAX_YEARS_ERROR,
    payload: { error },
  };
}

function receiveTaxYears(data: any) {
  return {
    type: TAX_YEARS_SUCCESS,
    payload: data,
  };
}

export function getTaxYears() {
  return async (dispatch: any, getState: any) => {
    const state = getState();
    if (state.sustainability) {
      dispatch(requestTaxYears());
      try {
        const data = await fetchTaxYears();
        dispatch(receiveTaxYears(data));
      } catch (error) {
        dispatch(errorTaxYears(error));
      }
    }
    return Promise.resolve;
  };
}

function getActiveYearInfo(
  tax_years: any,
  current_year: any,
  is_simulation: boolean,
  is_first_split: boolean
) {
  if (!current_year || !tax_years) { return { split: null, active: null }; }
  const activeYear = current_year.year + (is_simulation ? 0 : -1);
  let splitYear = tax_years.filter(
    (tax_year: any) => tax_year.year === activeYear
  );
  const activePeriod =
    !is_first_split && splitYear[1] ? splitYear[1] : splitYear[0];
  if (splitYear.length < 2) { splitYear = null; }
  return { split: splitYear, active: activePeriod };
}

// Reducer Definition
export default handleActions<MainSustainabilityState>(
  {
    [SUSTAINABILITY_TOGGLE_SIMULATION]: handleToggleSimulation,
    [SUSTAINABILITY_TOGGLE_JOINT]: handleToggleJoint,
    [SUSTAINABILITY_TOGGLE_SPLIT_YEAR]: handleToggleSplitYear,
    [TAX_YEARS_REQUEST]: handleTaxYearsRequest,
    [TAX_YEARS_ERROR]: handleTaxYearsError,
    [TAX_YEARS_SUCCESS]: handleTaxYearsSuccess,
  } as any,
  initialState
);

// Reducer Description
function handleToggleSimulation(
  state: MainSustainabilityState,
  action: Action<any>
) {
  const activeYearInfo = getActiveYearInfo(
    state.taxYears.data,
    state.taxYears.currentYear,
    !state.isSimulation,
    state.isFirstSplit
  );

  return {
    ...state,
    isSimulation: !state.isSimulation,
    splitYear: activeYearInfo.split,
    activePeriod: activeYearInfo.active,
    error: undefined as any,
  };
}

function handleToggleJoint(
  state: MainSustainabilityState,
  action: Action<any>
) {
  return {
    ...state,
    isJoint: !state.isJoint,
    error: undefined as any,
  };
}

function handleToggleSplitYear(
  state: MainSustainabilityState,
  action: Action<any>
) {
  const activeYearInfo = getActiveYearInfo(
    state.taxYears.data,
    state.taxYears.currentYear,
    state.isSimulation,
    !state.isFirstSplit
  );

  return {
    ...state,
    isFirstSplit: !state.isFirstSplit,
    activePeriod: activeYearInfo.active,
    error: undefined as any,
  };
}

function handleTaxYearsRequest(state: MainSustainabilityState) {
  return {
    ...state,
    taxYears: {
      ...state.taxYears,
      ...{ isFetching: true, error: undefined as any },
    },
  };
}

function handleTaxYearsError(
  state: MainSustainabilityState,
  action: Action<ErrorPayload>
) {
  return {
    ...state,
    taxYears: {
      ...state.taxYears,
      ...{ isFetching: false, error: action.payload.error },
    },
  };
}

function handleTaxYearsSuccess(
  state: MainSustainabilityState,
  action: Action<any>
) {
  const currentYear = action.payload.tax_years.filter(
    (tax_year: any) => tax_year.id === action.payload.current_tax_year_id
  )[0];
  const activeYearInfo = getActiveYearInfo(
    action.payload.tax_years,
    currentYear,
    state.isSimulation,
    state.isFirstSplit
  );

  return {
    ...state,
    taxYears: {
      ...state.taxYears,
      ...{
        isFetching: false,
        data: action.payload.tax_years,
        currentYear,
        error: undefined as any,
      },
    },
    splitYear: activeYearInfo.split,
    activePeriod: activeYearInfo.active,
  };
}
